/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
import store from '@/store';
import router from '@/router';
import globals from '@/globals';

import logger from '../../lib/logger';

import Config from './Config';
import { FaceTecSDK } from '../../../public/core-sdk/FaceTecSDK.js/FaceTecSDK';
import { LivenessCheckProcessor } from './processors/LivenessCheckProcessor';
import { SampleAppUtilities } from './utilities/SampleAppUtilities';
import ThemeHelpers from './utilities/ThemeHelpers';
import LocalizationText from './utilities/LocalizationText';
import { useSelfieParcelexFallback } from './utilities/fallbackFlow';

// eslint-disable-next-line import/prefer-default-export
export const FaceTec = (() => {
  const isSuperOnboarding = window.location.pathname.includes('super-onboarding');
  const parcelexOnboardingVersion = window.location.pathname.includes('onboarding-v2') || window.location.pathname.includes('link-de-pagamento') ? 'v2' : 'v1';
  const frontendOnboardingVersion = isSuperOnboarding ? 'minified' : parcelexOnboardingVersion;

  let latestEnrollmentIdentifier = '';
  let latestSessionResult = null;
  let latestIDScanResult = null;
  let latestProcessor;

  async function openSession() {
    return new Promise((resolve, reject) => {
      try {
        FaceTecSDK.setResourceDirectory('../../core-sdk/FaceTecSDK.js/resources');

        FaceTecSDK.setImagesDirectory('../../core-sdk/FaceTec_images');

        ThemeHelpers.setAppTheme(ThemeHelpers.getCurrentTheme());

        SampleAppUtilities.formatUIForDevice();

        FaceTecSDK.initializeInProductionMode(
          Config.ProductionKeyText,
          Config.DeviceKeyIdentifier,
          Config.PublicFaceScanEncryptionKey,
          async (initializedSuccessfully) => {
            if (initializedSuccessfully) {
              SampleAppUtilities.enableAllButtons();

              FaceTecSDK.configureLocalization(LocalizationText);

              return resolve({ success: true });
            }

            const { useFallback } = await useSelfieParcelexFallback({
              frontendOnboardingVersion,
              error: 'Cannot start session',
            });

            if (useFallback) return resolve({});

            const urlSearchParams = new URLSearchParams(window.location.search);
            const query = Object.fromEntries(urlSearchParams.entries());

            let errorPage = 'onboarding-facetec-error';

            if (frontendOnboardingVersion === 'minified') {
              errorPage = 'onboarding-minified-selfie-error';
            } if (frontendOnboardingVersion === 'v2') {
              errorPage = 'v2-onboarding-facetec-error';
            }

            return router.replace({
              name: errorPage,
              query,
            });
          },
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  function onServerSessionTokenError() {
    SampleAppUtilities.handleErrorGettingServerSessionToken();
  }

  async function getSessionToken(sessionTokenCallback) {
    let onboardingId = store.getters['onboarding/onboardingId'];

    if (frontendOnboardingVersion === 'minified') {
      onboardingId = store.getters['minifiedOnboarding/onboardingId'];
    } if (frontendOnboardingVersion === 'v2') {
      onboardingId = store.getters['onboardingV2/onboardingId'];
    }

    const token = store.getters['auth/token'];

    let onboardingApi = '/onboarding/v2/';

    if (frontendOnboardingVersion === 'minified') {
      onboardingApi = '/onboarding/v3/';
    }

    const XHR = new XMLHttpRequest();
    XHR.open('GET', `${Config.BaseURL}${onboardingApi}${onboardingId}/selfie/session-token`);
    XHR.setRequestHeader('X-Device-Key', Config.DeviceKeyIdentifier);
    XHR.setRequestHeader('X-User-Agent', FaceTecSDK.createFaceTecAPIUserAgentString(''));
    XHR.setRequestHeader('Authorization', `Bearer ${token}`);

    // eslint-disable-next-line func-names
    XHR.onreadystatechange = function () {
      if (this.readyState === XMLHttpRequest.DONE) {
        let sessionToken = '';
        try {
          // Attempt to get the sessionToken from the response object.
          sessionToken = JSON.parse(this.responseText).sessionToken;
          // Something went wrong in parsing the response. Return an error.
          if (typeof sessionToken !== 'string') {
            onServerSessionTokenError();
            return;
          }
        } catch {
          // Something went wrong in parsing the response. Return an error.
          onServerSessionTokenError();
          const urlSearchParams = new URLSearchParams(window.location.search);
          const query = Object.fromEntries(urlSearchParams.entries());

          logger.error(new Error('Something went wrong in parsing the response'));
          let cameraRoute = 'onboarding-error';

          if (window.location.pathname === '/super-onboarding/selfie') {
            cameraRoute = 'onboarding-minified-selfie-error';
          } if (window.location.pathname === '/onboarding-v2/selfie/camera') {
            cameraRoute = 'v2-onboarding-error';
          }

          router.replace({
            name: cameraRoute,
            query,
          });
          return;
        }
        sessionTokenCallback(sessionToken);
      }
    };

    // eslint-disable-next-line func-names
    XHR.onerror = function () {
      onServerSessionTokenError();
    };
    XHR.send();
  }

  function onLivenessCheckPressed() {
    SampleAppUtilities.fadeOutMainUIAndPrepareForSession();

    // Get a Session Token from the FaceTec SDK, then start the 3D Liveness Check.
    getSessionToken((sessionToken) => {
      latestProcessor = new LivenessCheckProcessor(sessionToken, FaceTec);
    });
  }

  function onComplete() {
    SampleAppUtilities.showMainUI();

    if (!latestProcessor.isSuccess()) {
      // Reset the enrollment identifier.
      latestEnrollmentIdentifier = '';

      // Show early exit message to screen.  If this occurs, please check logs.
      SampleAppUtilities.displayStatus('Session exited early, see logs for more details.');

      return;
    }

    // Show successful message to screen
    SampleAppUtilities.displayStatus('Success');
  }

  function setLatestSessionResult(sessionResult) {
    latestSessionResult = sessionResult;
  }

  function setIDScanResult(idScanResult) {
    latestIDScanResult = idScanResult;
  }

  function getLatestEnrollmentIdentifier() {
    return latestEnrollmentIdentifier;
  }

  function setLatestServerResult(responseJSON) {
    return responseJSON;
  }

  return {
    openSession,
    onLivenessCheckPressed,
    onComplete,
    setLatestSessionResult,
    setIDScanResult,
    getLatestEnrollmentIdentifier,
    setLatestServerResult,
  };
})();
